// RuleEditorGeneral.tsx
import React, { useEffect, useState } from 'react';
import { Popover, Select } from 'antd';
import FormField from '../../../../shared/edit-row-modal/form-field/form-field';
import { Toggle } from '../../../../shared/toggle/toggle';
import '../../../../shared/edit-row-modal/data-editor-form.scss';
import './rule-editor-general.scss';
import { EditableRule } from '../../../../../model/EditableRule';
import { FieldMessage, getMandatoryErrorMessage } from '../../../../../dtos/error-message.dto';
import useCustomModal from '../../../../shared/custom-modal/custom-modal';
import { DisableReasons, ProfilesOptions } from '../../../../../utils/enums';
import { useStores } from '../../../../store';
import Arrow from '@svg/chevron-down_20.svg?react';
import { CustomInput } from 'src/components/shared/custom-input/custom-input';

const DeactivateRuleModalBody = () => <>
    <div className="modal-commit-message">
        By Deactivating this rule, its restrictions will not be enforced once the draft policy version is applied.
    </div>
    <div className="modal-commit-message">
        You can always reactivate this rule again.
    </div>
</>;

interface RulesListContentProps {
    rules: { id: string | number; displayName: string }[];
}

const RulesListContent: React.FC<RulesListContentProps> = ({ rules }) => <>
    {rules.map((rule, index) =>
        <div className={'rule-item'} key={rule.id}>
            <span className={'rule-position'}>{index + 1}</span>
            <span>{rule.displayName}</span>
        </div>
    )}
</>;

interface RuleEditorGeneralProps {
    ruleData: EditableRule;
    sectionErrors: { [field: string]: FieldMessage };
    handleRuleChange: (rule: Partial<EditableRule>) => void;
    ruleCount: number;
}

const RuleEditorGeneral: React.FC<RuleEditorGeneralProps> = ({
    ruleData,
    sectionErrors,
    handleRuleChange,
    ruleCount,
}) => {

    const [showModal, , contextHolder] = useCustomModal();
    const { fwVersionStore } = useStores();
    const [showRulesList, setShowRulesList] = useState(false);

    const positionOptions =
        Array.from(
            { length: ruleData.id ? ruleCount - 1 : ruleCount },
            (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` })
        );

    const actionOptions = [
        { label: `Allow`, value: `allow` },
        { label: `Drop`, value: `drop` },
    ];

    const handleActiveChange = (value: boolean) => {
        if (!value && ruleData.id) {
            showModal({
                wrapClassName: 'change-zone-modal-wrapper',
                width: 480,
                title: 'Deactivate Rule',
                body: DeactivateRuleModalBody,
                okText: 'Deactivate',
                cancelText: 'Cancel',
                onOk: () => handleRuleChange({ active: value }),
                closable: false
            });
        }
        else {
            handleRuleChange({ active: value });
        }
    };

    const handleChangeAction = (value: 'allow' | 'drop') => {
        const updatedData: Partial<EditableRule> = {
            action: value,
            antiVirus: value === 'drop' ? ProfilesOptions.INACTIVE : ruleData.antiVirus,
            antiSpyware: value === 'drop' ? ProfilesOptions.INACTIVE : ruleData.antiSpyware,
            vulnerabilityProtection: value === 'drop' ? ProfilesOptions.INACTIVE : ruleData.vulnerabilityProtection,
        };
        handleRuleChange(updatedData);
    };

    useEffect(() => {
        if (!ruleData.isDefault && !ruleData.action) {
            handleChangeAction('allow');
        }
    }, []);


    return (
        <div className="rule-editor-general data-editor-form">
            <div className="form-fields">
                <FormField label={'Action'} errorMessage={getMandatoryErrorMessage(sectionErrors['action'])}>
                    <Select
                        disabled={ruleData.isDefault}
                        value={ruleData.action}
                        onChange={handleChangeAction}
                        placeholder={'Select'}
                        options={actionOptions}
                        style={{ width: 120 }}
                        popupClassName="dropdown-custom"
                        suffixIcon={<Arrow />}
                    />
                </FormField>
                <FormField label={'Position'} errorMessage={getMandatoryErrorMessage(sectionErrors['position'])}>
                    <Select
                        virtual={false}
                        disabled={ruleData.isDefault}
                        value={ruleData.position}
                        placeholder={'Select'}
                        onChange={(value) => handleRuleChange({ position: value })}
                        options={positionOptions}
                        style={{ width: 120 }}
                        popupClassName="dropdown-custom"
                        onDropdownVisibleChange={(open) => setShowRulesList(open)}
                        suffixIcon={<Arrow />}
                    />
                    <Popover
                        content={<RulesListContent rules={fwVersionStore.selectedVersionRules} />}
                        showArrow={true}
                        placement={'rightTop'}
                        overlayClassName={'rules-list'}
                        trigger={'click'}
                        open={showRulesList}
                        className={'view-rules-list'}
                    >
                        <button type="button" className="view-rules-list-btn" onClick={() => setShowRulesList(!showRulesList)}>View Rules List</button>
                    </Popover>
                </FormField>
                <FormField label={ruleData.active ? 'ACTIVE' : 'INACTIVE'} inlineLabel={true}>
                    <Toggle
                        disableReason={ruleData.name === 'Drop All' ? DisableReasons.CAN_NOT_CHANGE : undefined}
                        value={ruleData.active}
                        onChange={handleActiveChange}
                    />
                </FormField>
                <FormField label={'Description'} errorMessage={sectionErrors['description']?.message}>
                    <CustomInput inputType="TEXTAREA"
                        value={ruleData.description}
                        hideWarnMessage={!!sectionErrors['description']?.message}
                        onChange={(e) => handleRuleChange({ description: e.target.value })}
                        maxLength={50}
                        disabled={ruleData.isDefault}
                        rows={4} />
                </FormField>
                {contextHolder}
            </div>
        </div>
    );
};

export default RuleEditorGeneral;